import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Save } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";

const Cell = ({ value, onChange, w = "w-20", type = "text", testid }) => (
  <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
    className={`${w} bg-[#0B101D] border border-white/10 rounded px-2 py-1 text-xs font-mono outline-none focus:border-blue-500`} />
);

export const ReviewTable = ({ meeting, setMeeting, onSaved }) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const setField = (k, v) => setMeeting({ ...meeting, [k]: v });
  const setRace = (ri, k, v) => setMeeting({ ...meeting, races: meeting.races.map((r, i) => (i === ri ? { ...r, [k]: v } : r)) });
  const setRunner = (ri, ni, k, v) => setRace(ri, "runners", meeting.races[ri].runners.map((r, i) => (i === ni ? { ...r, [k]: v } : r)));
  const dropRunner = (ri, ni) => setRace(ri, "runners", meeting.races[ri].runners.filter((_, i) => i !== ni));

  const save = async () => {
    setBusy(true);
    try { await api("post", "/admin/meetings", meeting); toast.success(`${meeting.track} saved and live`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <section className="rounded-2xl bg-[#131C31] border border-blue-500/30 p-5 rise" data-testid="admin-review-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="font-display text-lg font-bold mr-auto">Review before saving</h2>
        <button data-testid="admin-review-cancel-button" onClick={() => setMeeting(null)} className="px-4 py-2 rounded-full text-sm text-slate-400 hover:text-white">Discard</button>
        <button data-testid="admin-review-save-button" onClick={save} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold disabled:opacity-50">
          <Save className="w-4 h-4" />{busy ? "Saving…" : "Save meeting"}
        </button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5 text-xs text-slate-400">
        {[["track", "Track", "w-40"], ["date", "Date", "w-32"], ["state", "State", "w-16"], ["track_condition", "Condition", "w-24"], ["rail", "Rail", "w-24"], ["weather", "Weather", "w-24"]].map(([k, l, w]) => (
          <label key={k} className="flex flex-col gap-1">{l}<Cell testid={`review-meeting-${k}`} w={w} value={meeting[k]} onChange={(v) => setField(k, v)} /></label>
        ))}
      </div>
      {meeting.races.map((race, ri) => (
        <div key={ri} className="mb-5 rounded-xl border border-white/5 overflow-x-auto">
          <div className="flex flex-wrap items-center gap-2 p-3 bg-[#0F172A] text-xs">
            <span className="font-display font-bold text-sm">R{race.number}</span>
            <Cell testid={`review-race-${ri}-name`} w="w-56" value={race.name} onChange={(v) => setRace(ri, "name", v)} />
            <Cell w="w-20" type="number" value={race.distance} onChange={(v) => setRace(ri, "distance", v)} />m
            <Cell w="w-16" value={race.start_time} onChange={(v) => setRace(ri, "start_time", v)} />
          </div>
          <table className="w-full text-xs">
            <thead className="text-slate-500 text-left"><tr><th className="p-2">No</th><th>Horse</th><th>Bar</th><th>Wt</th><th>Jockey</th><th>Trainer</th><th>Starts</th><th /></tr></thead>
            <tbody>
              {race.runners.map((r, ni) => (
                <tr key={ni} className="border-t border-white/5" data-testid={`review-runner-${ri}-${ni}`}>
                  <td className="p-2"><Cell w="w-12" type="number" value={r.number} onChange={(v) => setRunner(ri, ni, "number", v)} /></td>
                  <td><Cell w="w-40" value={r.horse} onChange={(v) => setRunner(ri, ni, "horse", v)} /></td>
                  <td><Cell w="w-12" type="number" value={r.barrier} onChange={(v) => setRunner(ri, ni, "barrier", v)} /></td>
                  <td><Cell w="w-16" type="number" value={r.weight} onChange={(v) => setRunner(ri, ni, "weight", v)} /></td>
                  <td><Cell w="w-32" value={r.jockey} onChange={(v) => setRunner(ri, ni, "jockey", v)} /></td>
                  <td><Cell w="w-36" value={r.trainer} onChange={(v) => setRunner(ri, ni, "trainer", v)} /></td>
                  <td className="font-mono text-slate-400 px-2">{r.last_starts?.length || 0}</td>
                  <td><button onClick={() => dropRunner(ri, ni)} className="p-1.5 hover:text-rose-400" aria-label="remove"><Trash2 className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
};
